import { addDays, daysBetween, type Sprint } from '@hive/shared';
import type { Dependency } from '@/data/dependencies';
import type { GridTask } from '@/data/tasks';

// The Gantt view's layout, worked out from tasks, dependencies and sprints.
// Pure, so it's unit-tested without a browser.

export type GanttZoom = 'day' | 'week';

/** Pixels per day at each zoom. */
export const DAY_WIDTH: Record<GanttZoom, number> = { day: 32, week: 10 };
export const ROW_HEIGHT = 32;

export interface GanttBar {
  task: GridTask;
  row: number;
  /** First and last day it covers (inclusive). */
  start: string;
  end: string;
  /** Only one of start/due is set: drawn as a one-day marker. */
  marker: boolean;
}

/** A task's span: start to due; one date alone is a one-day marker; neither is unscheduled (null). */
export function taskSpan(
  task: Pick<GridTask, 'start_date' | 'due_date'>,
): { start: string; end: string; marker: boolean } | null {
  const { start_date: s, due_date: d } = task;
  if (s && d) return s <= d ? { start: s, end: d, marker: false } : { start: d, end: s, marker: false };
  const only = s ?? d;
  return only ? { start: only, end: only, marker: true } : null;
}

/** Scheduled tasks as bars (one row each, by start date then due date), and the unscheduled ones. */
export function layoutBars(tasks: readonly GridTask[]): { bars: GanttBar[]; unscheduled: GridTask[] } {
  const scheduled: { task: GridTask; span: NonNullable<ReturnType<typeof taskSpan>> }[] = [];
  const unscheduled: GridTask[] = [];
  for (const task of tasks) {
    const span = taskSpan(task);
    if (span) scheduled.push({ task, span });
    else unscheduled.push(task);
  }
  scheduled.sort(
    (a, b) =>
      a.span.start.localeCompare(b.span.start) ||
      a.span.end.localeCompare(b.span.end) ||
      a.task.position.localeCompare(b.task.position),
  );
  return {
    bars: scheduled.map(({ task, span }, row) => ({ task, row, ...span })),
    unscheduled,
  };
}

/** The dates the chart covers: every bar and sprint, today, and some room either side. */
export function ganttRange(
  bars: readonly Pick<GanttBar, 'start' | 'end'>[],
  sprints: readonly Pick<Sprint, 'start_date' | 'end_date'>[],
  today: string,
): { start: string; end: string; days: number } {
  let start = addDays(today, -7);
  let end = addDays(today, 21);
  for (const b of bars) {
    if (b.start < start) start = b.start;
    if (b.end > end) end = b.end;
  }
  for (const s of sprints) {
    if (s.end_date >= start && s.start_date < start) start = s.start_date;
    if (s.start_date <= end && s.end_date > end) end = s.end_date;
  }
  start = addDays(start, -2);
  end = addDays(end, 3);
  return { start, end, days: daysBetween(start, end) + 1 };
}

/** The x of the start of a day. */
export function dayX(date: string, rangeStart: string, zoom: GanttZoom): number {
  return daysBetween(rangeStart, date) * DAY_WIDTH[zoom];
}

/** Bar geometry: x from the start of its first day to the end of its last. */
export function barBox(bar: Pick<GanttBar, 'start' | 'end' | 'row'>, rangeStart: string, zoom: GanttZoom) {
  const x = dayX(bar.start, rangeStart, zoom);
  return {
    x,
    width: (daysBetween(bar.start, bar.end) + 1) * DAY_WIDTH[zoom],
    y: bar.row * ROW_HEIGHT + 6,
    height: ROW_HEIGHT - 12,
  };
}

export interface GanttArrow {
  dependency: Dependency;
  path: string;
  /** The blocked task starts before its blocker is due. */
  conflict: boolean;
}

/** Arrows from each blocker's end to the blocked task's start, for pairs both on the chart. */
export function dependencyArrows(
  dependencies: readonly Dependency[],
  bars: readonly GanttBar[],
  rangeStart: string,
  zoom: GanttZoom,
): GanttArrow[] {
  const byTask = new Map(bars.map((b) => [b.task.id, b]));
  return dependencies.flatMap((dependency) => {
    const from = byTask.get(dependency.blocker_id);
    const to = byTask.get(dependency.blocked_id);
    if (!from || !to) return [];
    const a = barBox(from, rangeStart, zoom);
    const b = barBox(to, rangeStart, zoom);
    const x1 = a.x + a.width;
    const y1 = a.y + a.height / 2;
    const x2 = b.x;
    const y2 = b.y + b.height / 2;
    // Out to the right, down or up to the target row, then in from the left;
    // when the target starts too early, loop back between the rows.
    const path =
      x2 - 16 >= x1
        ? `M${x1},${y1} H${x1 + 8} V${y2} H${x2}`
        : `M${x1},${y1} H${x1 + 8} V${(y1 + y2) / 2} H${x2 - 8} V${y2} H${x2}`;
    return [{ dependency, path, conflict: to.start < from.end }];
  });
}

export type DragMode = 'move' | 'start' | 'end';

/**
 * New dates after dragging a bar by `deltaDays`. Moving shifts both dates;
 * dragging an end changes that date, never past the other. A one-date marker
 * keeps only the date it had.
 */
export function draggedDates(
  task: Pick<GridTask, 'start_date' | 'due_date'>,
  mode: DragMode,
  deltaDays: number,
): { start_date: string | null; due_date: string | null } | null {
  if (deltaDays === 0) return null;
  const span = taskSpan(task);
  if (!span) return null;
  const shift = (d: string | null) => (d ? addDays(d, deltaDays) : null);
  if (span.marker || mode === 'move') return { start_date: shift(task.start_date), due_date: shift(task.due_date) };
  if (mode === 'start') {
    const start = addDays(span.start, deltaDays);
    return { start_date: start > span.end ? span.end : start, due_date: span.end };
  }
  const end = addDays(span.end, deltaDays);
  return { start_date: span.start, due_date: end < span.start ? span.start : end };
}

/** Day or week ticks for the header: [x, label, isMonthStart]. */
export function ganttTicks(
  range: { start: string; days: number },
  zoom: GanttZoom,
): { x: number; label: string; major: boolean }[] {
  const out: { x: number; label: string; major: boolean }[] = [];
  for (let i = 0; i < range.days; i++) {
    const date = addDays(range.start, i);
    const [, m, d] = date.split('-').map(Number) as [number, number, number];
    const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
    if (zoom === 'day' || weekday === 1) {
      out.push({
        x: i * DAY_WIDTH[zoom],
        label: zoom === 'day' ? String(d) : `${d}/${m}`,
        major: d === 1 || (zoom === 'day' && weekday === 1),
      });
    }
  }
  return out;
}
